import React from 'react';
import { Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { navigateWithinAdmin } from './tmeWikiNodeSupport';

/**
 * An inline link from one TME Wiki page to another.
 *
 * Planning/TME-WIKI-2026-09-02.md §7, §9.3, §9.5.1, D10, D19, R15.
 *
 * ⚠️ THE STORED MARKUP IS A PLAIN ANCHOR, for the same four reasons the script
 * node's is:
 *
 *     <a href="/admin/tme-wiki/wikis/twiki_01H…/pages/tpage_01H…">Enrolment</a>
 *
 * allow-listed tag and attribute, relative path, NO class, NO data-*, and NO
 * `target` - which the sanitiser would answer by forcing a `rel`, breaking the
 * byte-identical round trip.
 *
 * ⚠️ A CROSS-WIKI LINK IS LEGITIMATE (D19), which is why the href carries BOTH
 * ids. The target may also be ARCHIVED: that is shown rather than forbidden
 * (§9.5.1) - the deep link resolves and the page itself says so under a banner.
 */

export const PAGE_HREF_PREFIX = '/admin/tme-wiki/wikis/';
const PAGE_HREF_PATTERN = /^\/admin\/tme-wiki\/wikis\/([^/]+)\/pages\/([^/]+)$/;

export function pageHref(wikiId, pageId) {
    return `${PAGE_HREF_PREFIX}${wikiId}/pages/${pageId}`;
}

const PageLinkView = ({ node }) => {
    const { wikiId, pageId, label } = node.attrs;

    const open = (event) => {
        // Native navigation would leave the SPA - on preview, to a path outside
        // `/gantt`. The stored href stays plain and absolute so it degrades
        // gracefully; only the behaviour is intercepted.
        event.preventDefault();
        navigateWithinAdmin(pageHref(wikiId, pageId));
    };

    return (
        <NodeViewWrapper as="span" className="inline">
            <a
                href={pageHref(wikiId, pageId)}
                onClick={open}
                title="Open this wiki page"
                className="text-primary-600 underline hover:text-primary-800"
            >
                {label || 'wiki page'}
            </a>
        </NodeViewWrapper>
    );
};

const PageLinkNode = Node.create({
    name: 'tmeWikiPageLink',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
        return {
            wikiId: { default: null },
            pageId: { default: null },
            label: { default: '' }
        };
    },

    parseHTML() {
        return [{
            // ⚠️ THE PRIORITY IS ON THE PARSE RULE, NOT ON THE EXTENSION, and
            // it is load-bearing. Link's rule is `a[href]`, which matches these
            // anchors too; ProseMirror's DOMParser orders rules by their OWN
            // `priority` (default 50), so at the default the MARK wins - the
            // anchor renders with Link's class, `target` and `rel`, the node
            // never forms, and the click is not intercepted. Measured, not
            // assumed: an extension-level priority changed nothing.
            priority: 1000,
            tag: `a[href^="${PAGE_HREF_PREFIX}"]`,
            getAttrs: (element) => {
                const match = PAGE_HREF_PATTERN.exec(element.getAttribute('href') || '');
                if (!match) return false;
                return { wikiId: match[1], pageId: match[2], label: element.textContent || '' };
            }
        }];
    },

    renderHTML({ node }) {
        return ['a', { href: pageHref(node.attrs.wikiId, node.attrs.pageId) }, node.attrs.label || ''];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PageLinkView);
    },

    addCommands() {
        return {
            insertTmeWikiPageLink: attrs => ({ commands }) =>
                commands.insertContent({ type: this.name, attrs })
        };
    }
});

export default PageLinkNode;
